import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SxProps,
  Theme,
} from '@mui/material'
import { prop } from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { When } from 'react-if'
import { useParams } from 'react-router-dom'

import {
  Icon,
  minLength,
  Option,
  VailSelect,
  VailTextField,
} from '@/components'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useSmallScreen } from '@/hooks'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { useListEngagementGoals } from '@/screens/EngagementGoals/hooks/useListEngagementGoals'
import { camelize, replaceNullishValues, safeCypressID } from '@/utils'

export type SemanticQuestionProps = {
  buttonText: string
  buttonSx?: SxProps<Theme>
  questionKey?: string | null
}

type WorkshopButton = {
  action?: string
  label: string
  url?: string
  value?: string
  disabled?: boolean
}

type ChatPrompt = {
  role: string
  content: string
}

export type BubbleOptions = {
  value: string
}[]
export type relatedEgs = {
  egName: string
  buttonLabel: string
  egID: string
}[]
export type SemanticSearchSettings = {
  largeLanguageModelSettings: {
    adverseEventCheck: boolean | null
    adverseEventDetectedStep: string
    adverseEventDetectedStepRecipe: string
    allowSubmission: boolean
    defaultQuestionKeys: string[]
    defaultQuestionText: string
    haveGptChooseBestQuestion: boolean
    includeDefaultQuestions: boolean
    initialSearchText: string
    keywordQuestionNotFoundText: BubbleOptions
    keywordSimilarQuestionsFoundText: BubbleOptions
    keywordSimilarQuestionsQuantity: number
    openAiAdverseEventTurboPrompt: ChatPrompt[]
    openAiCompletionPrompt: string
    pickFromAnswerEmbeddings: boolean
    pineconeNamespace: string
    pineconeTopK: number
    questionNotFoundButtons: WorkshopButton[]
    questionNotFoundText: BubbleOptions
    relatedQuestionsQuantity: number
    returningSearchText: BubbleOptions
    searchInstructions: BubbleOptions
    semanticSearchOutroStep: string
    relatedEgsIntroText: BubbleOptions
    showSources: boolean
    similarQuestionsFoundButtons: WorkshopButton[]
    similarQuestionsFoundText: BubbleOptions
    submitQuestionButtonText: string
    submitQuestionCustomerEmail: string
    submitQuestionEnabled: boolean
    submitQuestionExtraBody: string
    submitQuestionText: string
    customOpenAiAdverseEventTurboPrompt: boolean
    thresholdToGoStraightToAnswer: number
  }
  questions: {
    question: string
    answerText: string
    key: string
    source: string | null
    suggestedQuestions?: string[]
    relatedEgs?: relatedEgs
  }[]
}

type Question = SemanticSearchSettings['questions'][number]
export function NewSemanticQuestion(props: SemanticQuestionProps) {
  const { questionKey, buttonText, buttonSx } = props
  const { componentID } = useComponentProvider()

  const semanticSearch =
    useGetCustomComponent<SemanticSearchSettings>(componentID)
  const updateSemanticSearch =
    useUpdateCustomComponent<SemanticSearchSettings>(componentID)

  const isSmallScreen = useSmallScreen()
  const form = useForm<Question>()
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'relatedEgs',
  })

  const appendRelatedEg = () =>
    append({ buttonLabel: '', egID: '', egName: '' })
  const removeRelatedEg = (index: number) => remove(index)
  const [showModal, setShowModal] = useState(false)

  const selectedQuestion = useMemo(
    () =>
      (semanticSearch.data?.settings?.questions?.find(
        (q) => q?.key === questionKey
      ) ?? {}) as Question,
    [semanticSearch.data?.settings?.questions, questionKey]
  )

  const { reset, setValue } = form

  const resetForm = useCallback(() => {
    if (!componentID)
      reset({
        answerText: '',
        question: '',
        source: '',
        suggestedQuestions: [],
      })

    Object.entries(selectedQuestion).forEach(([key, value]) => {
      if (!value) return
      //@ts-expect-error - Object.entries is not giving the correct typing for the key
      setValue(key, value)
    })
  }, [reset, selectedQuestion, componentID, setValue])
  const hideModalHandler = () => {
    setShowModal(false)
    reset({ answerText: '', question: '', source: '', suggestedQuestions: [] })
  }

  const showModalHandler = () => {
    resetForm()
    setShowModal(true)
  }

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      // Check if showSources is false and replace empty strings and undefined values with null
      if (
        !semanticSearch.data?.settings?.largeLanguageModelSettings?.showSources
      ) {
        data.source = data.source ? data.source : null
      }

      const cleanDefaults = replaceNullishValues(data, {
        relatedEgs: [],
        relatedQuestions: [],
        suggestedQuestions: [],
      }) as Question

      cleanDefaults.suggestedQuestions =
        cleanDefaults.suggestedQuestions?.filter(Boolean) || []

      const newQuestions = [...(semanticSearch.data?.settings?.questions ?? [])]

      if (data.key) {
        const objIndex =
          semanticSearch.data?.settings?.questions?.findIndex(
            (obj) => obj?.key === data.key
          ) ?? -1
        if (objIndex !== -1)
          newQuestions[objIndex] = {
            ...data,
          } as SemanticSearchSettings['questions'][0]
      } else {
        let key: string
        const keySet = new Set(
          semanticSearch.data?.settings?.questions?.map(prop<string>('key')) ??
            []
        )

        for (
          let i = (semanticSearch.data?.settings?.questions?.length ?? 0) + 1;
          i > 0;
          i--
        ) {
          if (keySet.has(i.toString())) continue
          key = i.toString()
          break
        }
        key ??= (Math.random() * 100).toString()
        newQuestions.push({
          ...cleanDefaults,
          key,
        } as SemanticSearchSettings['questions'][0])
      }

      await updateSemanticSearch.mutateAsync({
        id: componentID as string,
        settings: {
          ...semanticSearch.data?.settings,
          questions: newQuestions
            .map((question) => {
              // adding id to relatedEgs
              if (!question.relatedEgs) return question
              return {
                ...question,
                relatedEgs: question.relatedEgs?.map((eg) => {
                  const egID = engagementGoals.find(
                    ({ name }) => camelize(name) === eg.egName
                  )?.id
                  if (!egID) return null
                  return {
                    ...eg,
                    egID,
                  }
                }),
              }
            })
            .filter(Boolean) as Question[],
        },
      })
    } finally {
      hideModalHandler()
    }
  })

  const { orgID } = useParams()
  const engagementGoals = useListEngagementGoals(orgID).data ?? []
  const sourcesDisabled =
    !semanticSearch.data?.settings?.largeLanguageModelSettings?.showSources

  return (
    <>
      <Button
        id={safeCypressID(`${buttonText} open modal`)}
        sx={buttonSx}
        onClick={showModalHandler}
      >
        {buttonText}
      </Button>
      <Dialog
        fullWidth
        fullScreen={!isSmallScreen}
        maxWidth="md"
        open={showModal}
        onClose={hideModalHandler}
      >
        <FormProvider {...form}>
          <form onSubmit={onSubmit}>
            <DialogTitle>
              {questionKey ? 'Edit a' : 'Create a new'} question
            </DialogTitle>
            <DialogContent>
              <Stack gap={2} p={2}>
                <VailTextField
                  control={form.control}
                  label="Question"
                  name="question"
                />
                <VailTextField
                  multiline
                  control={form.control}
                  helperText="Each line is a new text bubble."
                  label="Answer (Text)"
                  minRows={5}
                  name="answerText"
                  rules={{
                    required: false,
                  }}
                />
                <Button
                  color="info"
                  sx={{
                    alignSelf: 'flex-end',
                  }}
                  variant="outlined"
                  onClick={appendRelatedEg}
                >
                  + Related Engagement Goal
                </Button>
                {fields.map((field, index) => (
                  <Stack
                    key={field.id}
                    direction={['column', null, 'row']}
                    gap={2}
                    justifyContent="space-between"
                  >
                    <VailSelect
                      control={form.control}
                      label="Engagement Goals"
                      name={`relatedEgs.${index}.egName`}
                      options={engagementGoals.map((eg) => ({
                        label: eg.name,
                        value: camelize(eg.name),
                      }))}
                      rules={{ required: true }}
                    />
                    <VailTextField
                      control={form.control}
                      label="Related Engagement Goal Menu Button Text"
                      name={`relatedEgs.${index}.buttonLabel`}
                      rules={{
                        minLength: minLength(3),
                        required: true,
                      }}
                    />
                    <IconButton onClick={() => removeRelatedEg(index)}>
                      <Icon name="delete" />
                    </IconButton>
                  </Stack>
                ))}

                <When
                  condition={
                    (semanticSearch.data?.settings?.questions?.length || 0) > 0
                  }
                >
                  <VailSelect
                    label="Related Questions"
                    name="suggestedQuestions"
                    options={
                      semanticSearch.data?.settings?.questions
                        ?.filter((q) => q.key !== questionKey)
                        ?.map((q) =>
                          q !== null
                            ? { label: q.question, value: q.key }
                            : null
                        ) as Option[]
                    }
                    rules={{ validate: () => true }}
                    SelectProps={{ multiple: true }}
                  />
                </When>
                <VailTextField
                  control={form.control}
                  disabled={sourcesDisabled}
                  label="Source"
                  name="source"
                  // This is to ensure that the source is null when showSources is false
                  rules={{
                    required: !sourcesDisabled,
                    validate: (value) => {
                      if (sourcesDisabled && !value) return true
                      if (typeof value === 'string' && value.length >= 3)
                        return true
                      return 'the minimum length of the source is 3'
                    },
                  }}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button variant="text" onClick={hideModalHandler}>
                Cancel
              </Button>
              <Button
                id={safeCypressID('save question')}
                loading={form.formState.isSubmitting}
                type="submit"
                variant="contained"
              >
                Save Question
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  )
}
